import { Fragment, memo, useEffect, useRef, useState } from 'react';
import {
  shouldFetchJobTable,
  markJobTableFetched,
  getCachedJobTableData,
  saveCachedJobTableData,
} from './dashboardFetchCache';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useTheme } from '@mui/material/styles';
import FuseLoading from '@fuse/core/FuseLoading';
import moment from 'moment';
import Zero from './Zero';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { red } from '@mui/material/colors';
import TableCell from '@mui/material/TableCell';
import CheckIcon from '@mui/icons-material/Check';
import Tooltip from '@mui/material/Tooltip';
import client from 'client';
import AuthAction from 'app/stores/loadz/redux/Auth/actions';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import store from 'app/store';
import {useTranslation} from 'react-i18next';
import OriginalJobLabel from 'app/main/loadz/components/OriginalJobLabel';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;
  const {t} = useTranslation('navigation');

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label='first page'
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

let idRow = 'progress';

/** `123-456` → `456` (sufijo tras el primer `-`; vista compacta new dashboard). */
function formatCompactJobNumberDisplay(composite) {
  const s = String(composite ?? '');
  const i = s.indexOf('-');
  return i >= 0 ? s.slice(i + 1) : s;
}

function Row(props) {
  const { row, iden, todayTotal, jobType, getDriverEarnings, getTicketData, compactView, history, onJobClick } = props;
  const [open, setOpen] = useState(false);

  const getStatus = (jobType) => {
    switch (jobType) {
      case 0:
        return 'in-progress';
      case 1:
        return 'completed';
      case 2:
        return 'in delayed';
      default:
        break;
    }
  };

  const getTodayTotal = (todayTotal, jobType) => {
    switch (jobType) {
      case 0:
        return todayTotal?.progressToday;
      case 1:
        return todayTotal?.completedToday;
      case 2:
        return todayTotal?.delayedToday;
      default:
        break;
    }
  };

  const handleOpen = (open, id) => {
    setOpen(!open);
  };

  const getFormattedOutput = (todayTotal, iden, jobType, row, compactView = false) => {
    if (todayTotal[iden]) {
      const fullfillmentTotal = row?.fullfillmentTotal || '';

      if (getStatus(jobType) === 'in-progress') {
        const t = todayTotal[iden];
        const inProg = Number(getTodayTotal(t, jobType)) || 0;
        const total = Number(t?.total ?? 0) || 0;
        const delivered = total - inProg;
        const line = `${inProg.toFixed(2)} (${row.unit}) In-Progress ${delivered.toFixed(2)} (Delivered) + In Prog. = ${total.toFixed(2)}`;
        return (
          <div style={compactView ? { fontSize: '0.875rem' } : {}}>
            {line}
          </div>
        );
      } else {
        return (
          <div style={compactView ? { fontSize: '0.875rem' } : {}}>{`${Number(row.qtyTotal).toFixed(2)} (${row.unit}) ${getStatus(jobType)}, ${Number(
            todayTotal[iden].total - todayTotal[iden].progressToday
          ).toFixed(2)} / ${fullfillmentTotal} ${t('Delivered')}`}</div>
        );
      }
    } else {
      return ``;
    }
  };
  const {t} = useTranslation('navigation');

  const openJob = () => {
    if (onJobClick) {
      onJobClick(row);
    } else {
      history.push(`/jobs/ticket/${row.job_id}`);
    }
  };

  return (
    <Fragment>
      <TableRow
        onClick={compactView ? openJob : undefined}
        sx={
          compactView
            ? {
                /* :first-of-type afecta el 1er th y el 1er td; :first-child solo la columna Job # */
                '& > *:first-child': { borderBottom: 'none' },
                '& > *:not(:first-child)': { borderBottom: '1px solid #e0e0e0' },
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }
            : { '& > *': { borderBottom: 'unset' } }
        }
      >
        <TableCell component='th' scope='row' className='h1 font-bold' data-headtext='Job # '>
          {!compactView && (
            <IconButton
              style={{ color: '#000000' }}
              aria-label='expand row'
              size='small'
              onClick={() => handleOpen(open, `${idRow}-${row.job}`)}
              id={`${idRow}-${row.job}`}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          {compactView ? (
            <span
              style={{
                textDecoration: 'none',
                color: 'inherit',
                fontSize: '0.875rem',
              }}
            >
              {`#${formatCompactJobNumberDisplay(
                row.jobType == 'sub'
                  ? `${row.companyNumberClon}-${row.job}`
                  : `${row.companyNumber}-${row.job}`
              )}`}
              <OriginalJobLabel job={row} variant='compact' />
            </span>
          ) : (
            <a
              href={`/jobs/ticket/${row.job_id}`}
              target='_blank'
              rel='noopener noreferrer'
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {row.jobType == 'sub'
                ? `${row.companyNumberClon}-${row.job}`
                : `${row.companyNumber}-${row.job}`}
              <OriginalJobLabel job={row} />
            </a>
          )}
        </TableCell>
        {!compactView && (
          <>
            <TableCell component='th' scope='row' data-headtext='Material '>
              {row.material}
            </TableCell>
            <TableCell align='center' data-headtext='Ticket Count'>
              {row.tickets.length}
            </TableCell>
          </>
        )}
        {!compactView && (
          <TableCell align='left' data-headtext='Status'>
            {getFormattedOutput(todayTotal, iden, jobType, row, compactView)}
          </TableCell>
        )}
        {!compactView && (
          <TableCell align='left' data-headtext=' Quarry'>
            {row.quarry}{' '}
          </TableCell>
        )}
        <TableCell align='left' data-headtext='Destination'>
          {compactView ? (
            <>
              <div style={{ fontSize: '0.875rem' }}>Destination: {row.destination}</div>
              {row.quarry && (
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px' }}>
                  Quary: {row.quarry}
                </div>
              )}
            </>
          ) : (
            <div>{row.destination}</div>
          )}
        </TableCell>
        {compactView && (
          <TableCell align='left' data-headtext='Status'>
            {getFormattedOutput(todayTotal, iden, jobType, row, compactView)}
          </TableCell>
        )}
      </TableRow>
      {!compactView && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={6}
            className='responsive-loads-table'
          >
            <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant='h6' gutterBottom component='div'></Typography>
              <Table size='small' aria-label='purchases'>
                <TableHead>
                  <TableRow style={{ backgroundColor: '#edca33' }}>
                    <TableCell className='font-bold'>{t('TICKET_NO')}</TableCell>
                    <TableCell className='font-bold'>{t('QTY')}</TableCell>
                    <TableCell className='font-bold'>{t('DRIVER')}</TableCell>
                    <TableCell className='font-bold'>{t('TRUCK_NO')}</TableCell>
                    <TableCell className='font-bold'>{t('SCALE')}</TableCell>
                    <TableCell className='font-bold'>{t('DATE_TIME_CREATED')}</TableCell>
                    <TableCell className='font-bold'>{t('DATE_TIME_UPDATED')}</TableCell>
                    <TableCell className='font-bold'>{t('OWNER')}</TableCell>
                    <TableCell align='center' className='font-bold'>
                    {t('WAIT_TIME')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.tickets.map((ticket, i) => (
                    <TableRow key={i}>
                      <TableCell
                        onClick={(e) => {
                          e.stopPropagation();
                          getTicketData(ticket.tk_id);
                        }}
                        data-headtext='Ticket No.'
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          },
                        }}
                      >
                        {ticket.ticket}
                      </TableCell>

                      <TableCell data-headtext='Qty '>{Number(ticket.qty).toFixed(2)}</TableCell>

                      <TableCell
                        onClick={(e) => {
                          e.stopPropagation();
                          if (ticket.ticketType != 'CLON-SUB') {
                            getDriverEarnings(ticket.driver_id, ticket.ticket_owner_compacctnum);
                            //console.log('ticket', ticket);
                          }

                          if (
                            ticket.ticketType == 'CLON-SUB' &&
                            ticket.ownerop == true &&
                            ticket.ownerop_owner ==
                              store.getState().loadz_auth?.AuthReducer?.user.company.compacctnum
                          ) {
                            getDriverEarnings(ticket.driver_id, ticket.ticket_owner_compacctnum);
                          }
                        }}
                        data-headtext='Driver'
                        sx={{
                          cursor:
                            ticket.ticketType != 'CLON-SUB'
                              ? 'pointer'
                              : ticket.ticketType == 'CLON-SUB' &&
                                ticket.ownerop == true &&
                                ticket.ownerop_owner ==
                                  store.getState().loadz_auth?.AuthReducer?.user.company.compacctnum
                              ? 'pointer'
                              : 'text',
                          '&:hover': {
                            backgroundColor:
                              ticket.ticketType != 'CLON-SUB'
                                ? 'rgba(0, 0, 0, 0.08)'
                                : ticket.ticketType == 'CLON-SUB' &&
                                  ticket.ownerop == true &&
                                  ticket.ownerop_owner ==
                                    store.getState().loadz_auth?.AuthReducer?.user.company
                                      .compacctnum
                                ? 'rgba(0, 0, 0, 0.08)'
                                : '',
                          },
                        }}
                      >
                        {ticket.driver}
                      </TableCell>

                      <TableCell data-headtext='Truck No.'>{ticket.truck}</TableCell>
                      <TableCell data-headtext='Scale'>{ticket.scale}</TableCell>
                      <TableCell data-headtext='Date Time Created'>
                        {moment(ticket.dateTime).format('LLL')}
                      </TableCell>
                      <TableCell data-headtext='Date Time Updated '>
                        {moment(ticket.dateTimeUpdate).format('LLL')}
                      </TableCell>
                      <TableCell data-headtext='Owner ' align='center'>
                        {ticket?.ownerop ? (
                          <Typography className='h3'>OP</Typography>
                        ) : ticket.ticketType == 'CLON-SUB' && ticket.cxBindCo ? (
                          <Typography className='h3'>SUB</Typography> //here to owner with x
                        ) : ticket.ticketType == 'sub' && ticket.cxBindCo ? (
                          //here to sub with √
                          <Tooltip title='You owner' placement='top'>
                            <CheckIcon style={{ color: 'green', fontSize: 'xx-large' }} />
                          </Tooltip>
                        ) : !ticket.ticketType && !ticket.cxBindCo ? (
                          //here to owner with √
                          <Tooltip title='You owner' placement='top'>
                            <CheckIcon style={{ color: 'green', fontSize: 'xx-large' }} />
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                      </TableCell>
                      <TableCell align='center' data-headtext='Wait Time< '>
                        {ticket.waitTime ? (
                          <IconButton size='large' onClick={() => {}}>
                            <PauseCircleOutlineIcon
                              sx={{ color: red[600] }}
                            ></PauseCircleOutlineIcon>
                          </IconButton>
                        ) : (
                          <IconButton size='large' onClick={() => {}}>
                            <PauseCircleOutlineIcon
                              style={{ color: '#000000' }}
                            ></PauseCircleOutlineIcon>
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell style={{ borderBottom: 'none' }} className='font-bold'>
                      {t('TOTAL')}
                    </TableCell>
                    <TableCell style={{ borderBottom: 'none' }} className='font-bold'>{`${Number(
                      row.qtyTotal
                    ).toFixed(2)} (${row.unit})`}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      )}
    </Fragment>
  );
}

function JobTable({
  refreshTable,
  ticketCount,
  setJobCount,
  setQuantity,
  jobType,
  compacct,
  logout,
  jobStatus,
  setSelectedDriver,
  setSelectedCompacctnum,
  getDriverEarnings,
  getTicketData,
  compactView = false,
  showHeader = false,
  headerTitle = '',
  filterValue = '',
  onFilterChange = null,
  onJobClick = null,
  /** compactView: 'reference' = altura tipo iPad Pro; 'compact' | 'expanded' ajustan vh */
  layoutBand = 'reference',
  /** New dashboard: la tabla llena la altura del card (columnas alineadas). */
  compactFillParent = false,
  /**
   * Un solo `JobTable` (New Dashboard) reutiliza un solo estado `rows` pero el toolbar
   * llama `refreshProgress/refreshCompleted/refreshDelayed` a la vez: hay que inscribir el
   * `getData` real solo en el `jobType` activo; el resto no-op para no pisar `setRows` con
   * datos de otro tipo.
   */
  dashboardTableRefreshGroup = null,
}) {
  const [rows, setRows] = useState([]);
  const [zero, setZero] = useState(false);
  const [todayTotal, setTodayTotal] = useState([]);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const fetchInProgressRef = useRef(false);
  const [visibleRowsCount, setVisibleRowsCount] = useState(10);
  const tableContainerRef = useRef(null);

  const handleScroll = (e) => {
    if (!compactView) return;
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && visibleRowsCount < rows.length) {
      setVisibleRowsCount(prev => Math.min(prev + 10, rows.length));
    }
  };

  const assignRefreshToParent = (getDataFn) => {
    if (dashboardTableRefreshGroup) {
      const noop = async () => undefined;
      const g = dashboardTableRefreshGroup;
      if (g.progress) g.progress.current = jobType === 0 ? getDataFn : noop;
      if (g.completed) g.completed.current = jobType === 1 ? getDataFn : noop;
      if (g.delayed) g.delayed.current = jobType === 2 ? getDataFn : noop;
    } else if (refreshTable) {
      refreshTable.current = getDataFn;
    }
  };

  const getData = async () => {
    if (!compacct) return;
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;
    try {
      setZero(false);
      setLoading(true);
      const response = await client.get(`/jobs/get-for-dashboard/${jobType}/${compacct}`);
      const data = response.data;

      setJobCount(data?.jobs ? data.jobs.length : 0);
      ticketCount(data?.countTicket ? data.countTicket : 0);
      setQuantity(0);
      setRows(data?.results ? data.results : []);

      if (!data?.results) {
        setZero(true);
      }

      setTodayTotal(data?.allToday ? data.allToday : []);
      saveCachedJobTableData(compacct, jobType, data);
    } catch (error) {
      setZero(true);
      setRows([]);
      console.log(error);
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  };

  useEffect(() => {
    if (compacct == null || compacct === undefined) {
      setLoading(false);
      assignRefreshToParent(getData);
      return;
    }

    const cached = getCachedJobTableData(compacct, jobType);
    if (cached) {
      setJobCount(cached?.jobs ? cached.jobs.length : 0);
      ticketCount(cached?.countTicket ? cached.countTicket : 0);
      setQuantity(0);
      setRows(cached?.results ? cached.results : []);
      setTodayTotal(cached?.allToday ? cached.allToday : []);
      setZero(!cached?.results);
      setLoading(false);
      assignRefreshToParent(getData);
      return;
    }

    if (fetchInProgressRef.current) {
      assignRefreshToParent(getData);
      return;
    }

    markJobTableFetched(compacct, jobType);
    getData();
    assignRefreshToParent(getData);
  }, [compacct, jobType]);

  /**
   * Vista compacta (New Dashboard): el contador In-Progress/Completed usa el total del API (`jobs.length`);
   * antes solo se renderizaban 10 filas y el resto al hacer scroll, lo que parecía inconsistencia.
   * Si el total es ≤ 50, mostramos todas las filas; si hay más, ventana inicial de 10 + scroll infinito.
   */
  const COMPACT_VIEW_SHOW_ALL_MAX = 50;

  useEffect(() => {
    if (!compactView) return;
    const n = rows?.length ?? 0;
    if (!n) {
      setVisibleRowsCount(10);
      return;
    }
    if (n <= COMPACT_VIEW_SHOW_ALL_MAX) {
      setVisibleRowsCount(n);
    } else {
      setVisibleRowsCount(10);
    }
  }, [compactView, jobType, rows?.length]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const {t} = useTranslation('navigation');

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (zero && !compactView) {
    return <Zero value={jobStatus} />;
  }

  if (rows?.length === 0 && !compactView) {
    return <FuseLoading />;
  } else {
    return (
      <TableContainer 
        component={Paper} 
        className='responsive-loads-table' 
        sx={{
          backgroundColor: 'rgb(245, 245, 245)',
          ...(compactView &&
            (compactFillParent
              ? {
                  flex: { md: 1 },
                  minHeight: { xs: 220, md: 0 },
                  height: { xs: 'min(44vh, 380px)', md: '100%' },
                  maxHeight: { xs: 400, md: 'none' },
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }
              : {
                  height:
                    layoutBand === 'compact'
                      ? { xs: '28vh', sm: '34vh', md: '40vh' }
                      : layoutBand === 'expanded'
                        ? '52vh'
                        : '46vh',
                  maxHeight:
                    layoutBand === 'compact'
                      ? { xs: 320, sm: 380, md: 440 }
                      : layoutBand === 'expanded'
                        ? 560
                        : 500,
                  overflow: 'auto',
                })),
        }}
        onScroll={handleScroll}
        ref={tableContainerRef}
      >
        {showHeader && (
          <div className='flex justify-between items-center' style={{ padding: '16px 16px 12px 16px' }}>
            <Typography className='h6 font-Regular' style={{ fontWeight: 600, color: '#000000' }}>
              {headerTitle}
            </Typography>
            <FormControl size='extra-small'
              sx={{
                '& > :first-child': {
                  paddingBottom: '3px !important',
                  paddingTop: '3px !important',
                },
              }}>
              <Select
                value={filterValue}
                onChange={onFilterChange}
                sx={{
                  minWidth: 120,
                  borderRadius: '50px',
                  fontSize: '1.0rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#edca33',
                    borderRadius: '50px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#edca33',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#edca33',
                  },
                  '& .MuiSelect-select': {
                    paddingBottom: '0px',
                    paddingTop: '0px',
                    paddingLeft: '0px',
                    paddingRight: '16px !important',
                  },
                }}
              >
                <MenuItem value='inProgress'>In Progress</MenuItem>
                <MenuItem value='completed'>Completed</MenuItem>
                <MenuItem value='delayed'>Delayed</MenuItem>
              </Select>
            </FormControl>
          </div>
        )}
        {(zero || rows?.length === 0) && compactView ? (
          <Box
            sx={{
              flex: compactFillParent ? 1 : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 0,
              padding: '24px 16px',
              textAlign: 'center',
            }}
          >
            <Typography
              style={{
                fontSize: '1.75rem',
                color: '#666',
              }}
            >
              {loading && rows?.length === 0 ? 'Loading...' : 'No Reported Activity'}
            </Typography>
          </Box>
        ) : (
          <Table aria-label='collapsible table' sx={compactView ? { '& .MuiTableCell-root': { fontSize: '0.875rem', padding: '8px' } } : {}}>
          {!compactView && (
            <TableHead>
              <TableRow></TableRow>
            </TableHead>
          )}
          <TableBody className='responsive-loads-table'>
            {!compactView && (
              <TableRow>
                <TableCell className=' font-bold'>{t('JOB_HASTAG')}</TableCell>
                <TableCell className='font-bold'>{t('MATERIAL')}</TableCell>
                <TableCell className='font-bold'>{t('TICKET_COUNT')}</TableCell>
                <TableCell className='font-bold'>{t('STATUS')}</TableCell>
                <TableCell className='font-bold'>{t('QUARRY')}</TableCell>
                <TableCell className='font-bold'>{t('DESTINATION')}</TableCell>
              </TableRow>
            )}

            {(compactView 
              ? rows?.slice(0, visibleRowsCount)
              : (rowsPerPage > 0
                ? rows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : rows)
            )?.map((row, i) => (
              <Row
                key={i}
                row={row}
                iden={i}
                todayTotal={todayTotal}
                jobType={jobType}
                setSelectedDriver={setSelectedDriver}
                setSelectedCompacctnum={setSelectedCompacctnum}
                getDriverEarnings={getDriverEarnings}
                getTicketData={getTicketData}
                compactView={compactView}
                history={history}
                onJobClick={onJobClick}
              />
            ))}
          </TableBody>
          {!compactView && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={6}
                  count={rows?.length ? rows.length : 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page',
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
        )}
      </TableContainer>
    );
  }
}
const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(AuthAction.logout()),
});

export default connect(null, mapDispatchToProps)(memo(JobTable));
